import { log } from '@/log'
import { ApiCreditcard } from '@/models/api/creditcard'
import { Payment as ApiPayment } from '@/models/api/payment/payment'
import { CreditCardType } from '@/models/app-config/behavior/types'
import { ResolverResource } from '@/services/payment/variable-resolver'
import { PurchaseService } from '@spa/services/api/purchase'
import { ActionTree } from 'vuex'

import { DeliveryMethodDetail, OkihaiPlace } from '@/models/delivery'
import { RootState } from '../../types'
import { AppliedPoint, DeliveryConfig, OrderConfirmState, Payment, SelectedCoupon } from './types'

export interface WithApiPayment {
  apiPayment: ApiPayment,
}
export type PayloadSetPaymentToCreditcard =
  Pick<ApiCreditcard, 'numberLast4' | 'cardType' | 'lastCreditDate'>
  & {
    token: string
    payment_type: CreditCardType
  }
  & WithApiPayment

/*
 * 各支払い方法個別の action は
 * https://github.com/my-color/front/pull/7111/commits/019791154f6e80020c00a5d8591abb07b6db5549
 * で Order.vue / ApplySubscription.vue からは削除したが、支払い方法選択画面でも使われていたので
 * https://github.com/my-color/front/pull/7111
 * https://github.com/my-color/front/issues/4300
 * の対応時点ではまだ各種支払い方法ごとの action はまだ残っている.
 *
 * 最終的には単一のdispatchにまとめてしまい、支払い方法毎の初期化・設定値構築は
 * 独立・再利用可能なモジュールとして
 * client/spa/src/components/pages/order/model/payment/initialization/builder.ts
 * に定義したい.
 */
export const actions: ActionTree<OrderConfirmState, RootState> = {
  setPaymentToCreditcard(
    { commit },
    payload: PayloadSetPaymentToCreditcard
  ) {
    commit('setPayment', { payment: payload })
  },

  setPaymentToCashOnDelivery(
    { commit },
    payload: WithApiPayment
  ) {
    commit('setPayment', {
      payment: {
        payment_type: 'cash-on-delivery',
        ...payload,
      },
    })
  },

  setPaymentToNpPostpay(
    { commit },
    payload: WithApiPayment
  ) {
    commit('setPayment', {
      payment: {
        payment_type: 'np-postpay',
        ...payload,
      },
    })
  },

  setPaymentToAtone(
    { commit },
    payload: WithApiPayment
  ) {
    commit('setPayment', {
      payment: {
        payment_type: 'atone',
        paymentid: '',
        auth_token: '',
        ...payload,
      },
    })
  },

  setPaymentToPaidy(
    { commit },
    payload: WithApiPayment
  ) {
    commit('setPayment', {
      payment: {
        payment_type: 'paidy',
        paymentid: '',
        ...payload,
      },
    })
  },

  setPayment({ commit }, payload: { payment: Payment }) {
    commit('setPayment', payload)
  },

  unsetPayment({ commit }) {
    commit('unsetPayment')
  },

  putPaymentVariableResource({ commit }, payload: { partialResource: Partial<ResolverResource> }) {
    commit('putPaymentVariableResource', payload)
  },

  async initializeDelivery({ commit }, payload: { id: string }) {
    const purchase = new PurchaseService()
    const temporallyPreset = await purchase.getMenu().then(result => result.fold(
      (error): string => {
        log.error(error)

        throw error
      },
      (menu): string => menu.body.data.deliveryMethod.methods[0].name
    ))
    commit('initializeDelivery', { ...payload, presetMethod: temporallyPreset })
  },

  setDeliveryMethod({ commit }, payload: { method: DeliveryMethodDetail }) {
    commit('setDeliveryMethod', payload)
  },

  unsetDelivery({ commit }) {
    commit('unsetDelivery')
  },

  setDeliveryDate({ commit }, payload: { deliveryPlanDate: string, deliveryTimeSlot: string }) {
    commit('setDeliveryDate', payload)
  },

  selectCoupon({ commit }, payload: SelectedCoupon) {
    commit('selectCoupon', payload)
  },

  initCoupon({ commit }) {
    const noCoupon: SelectedCoupon = {
      selected: null,
    }
    commit('selectCoupon', noCoupon)
  },

  applyPoint({ commit }, payload: AppliedPoint) {
    commit('applyPoint', payload)
  },

  initAppliedPoint({ commit }) {
    commit('applyPoint', { applied :0, discounted: 0 })
  },

  setComplete({ commit }, payload) {
    commit('setComplete', payload)
  },

  setSelectedDeliveryConfig({ commit }, payload: DeliveryConfig) {
    commit('setSelectedDeliveryConfig', payload)
  },

  setOkihaiPlace({ commit }, payload: OkihaiPlace | null) {
    commit('setOkihaiPlace', payload)
  },
}
